import React from 'react';
import { Typography, Box, styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Courtage, Fund } from '../../api/useFunds';
import { toEuroWholeNumber } from '../../utils/numberFormat';
import { CloseIcon } from '../../components/CloseIcon';
import { bicToName } from '../../utils/bicMappings';
import { LightTooltip, WarnIcon } from './components';

const FundContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(3),
}));

type FundInputProps = {
  fund: Fund;
  bic: Bic | 'OTHER_BANK';
  investedAmount: number;
  onRemove: (fund: Fund) => void;
  onEdit: (fund: Fund) => void;
  isAddedExternally?: boolean;
};

export const FundInputMobile = ({
  fund,
  bic,
  investedAmount,
  onEdit,
  onRemove,
}: FundInputProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const compareBic = bic === 'OTHER_BANK' ? 'COBADEHDXXX' : bic;

  const courtage = fund.courtages?.find((c: Courtage) => {
    return c.bic === compareBic;
  });

  return (
    <FundContainer>
      <Box onClick={() => onEdit(fund)}>
        <Typography variant="body1" color="textPrimary">
          {fund.name}
          {!courtage && (
            <span
              style={{
                position: 'relative',
                display: 'inline-block',
                width: '20px',
                height: '20px',
              }}
            >
              <LightTooltip
                title={t('cashbackCalculator.detailed.fundInput.notAvailableAt', {
                  bank: bicToName(bic),
                })}
              >
                <WarnIcon
                  sx={{
                    position: 'absolute',
                    left: '7px',
                    top: '4px',
                    fontSize: '20px',
                  }}
                  data-testid="fund-not-available-warning"
                />
              </LightTooltip>
            </span>
          )}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.primary }}
          data-testid="isin-and-amount"
        >
          {fund.isin} • {toEuroWholeNumber(investedAmount)}
        </Typography>
      </Box>
      <CloseIcon
        data-testid={`remove-fund-${fund.isin}`}
        onClick={() => {
          onRemove(fund);
        }}
      />
    </FundContainer>
  );
};
